/**
 * Multi-Births by Generation — the rows and the count, both read off the same
 * `/dashboard-statistics-with-details` payload every other dashboard card uses.
 *
 * There is no multi-birth endpoint: a set is recorded on the member rows
 * themselves (`multiple_births_type` on the member the form was filled from,
 * `multiple_birth_details` links on the rest of the set), so the report is the
 * whole-tree member list filtered down to everyone who belongs to a set. The
 * card count and the detail page therefore agree by construction — they call the
 * same function on the same response.
 *
 * If the API later grows a section of its own, `extractMultiBirthMembers` picks
 * it up ahead of the derived list without either caller changing.
 */

import { getMultipleBirthSetSize } from "./dashboardMultipleBirthSuperscript";
import { extractAllMembersFromStatisticsResponse } from "./dashboardFamilyLineNavigation";
import { getMemberDobTimestamp } from "./family-tree/dateAndTime";

/** Section keys the API could name a ready-made multi-births block. */
const MULTI_BIRTH_SECTION_KEYS = [
  "multi_births_by_generation",
  "multiple_births_by_generation",
  "multi_births",
  "multiple_births",
];

const isNonEmptyArray = (value) => Array.isArray(value) && value.length > 0;

/**
 * A section's rows, flattened. The grouped shape the other sections use
 * (`details: [{ generation, details: [...] }]`) is unrolled onto its members,
 * with the group's generation stamped on each row so it survives the flatten.
 */
function flattenMultiBirthSection(section) {
  const rows = isNonEmptyArray(section?.details) ? section.details : section;
  if (!isNonEmptyArray(rows)) return [];

  return rows.flatMap((row) => {
    if (!isNonEmptyArray(row?.details)) return [row];
    const generation = row?.generation ?? row?.generation_level ?? row?.group;
    return row.details.map((member) =>
      generation === undefined ? member : { generation, ...member }
    );
  });
}

const memberKey = (member, index) =>
  member?.id ??
  member?.family_member_id ??
  member?.member_id ??
  `multi-birth-${index}`;

/** Same member reached through two sections is still one row. */
function dedupeMembers(members) {
  const seen = new Set();
  return members.filter((member, index) => {
    const key = String(memberKey(member, index));
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Everyone in the tree who belongs to a multiple-birth set.
 *
 * @param {object} data the `data` block of /dashboard-statistics-with-details
 * @returns {Array<object>} member rows, unsorted
 */
export function extractMultiBirthMembers(data) {
  if (!data) return [];

  const section = MULTI_BIRTH_SECTION_KEYS.map((key) => data[key]).find(
    (value) => isNonEmptyArray(value) || isNonEmptyArray(value?.details)
  );

  if (section) return dedupeMembers(flattenMultiBirthSection(section));

  const members = extractAllMembersFromStatisticsResponse(data).filter(
    (member) => getMultipleBirthSetSize(member) !== null
  );

  return dedupeMembers(members);
}

/**
 * The number on the Multi-Births by Generation card.
 *
 * A section that states its own count is taken at its word; otherwise it is the
 * number of people the rows list, which is what the detail page's header shows.
 */
export function getMultiBirthsByGenerationCount(data) {
  if (!data) return 0;

  const counted = MULTI_BIRTH_SECTION_KEYS.map((key) => data[key]?.count).find(
    (count) => typeof count === "number"
  );
  if (typeof counted === "number") return counted;

  return extractMultiBirthMembers(data).length;
}

/** Generation number, or null where the payload doesn't carry one. */
export function getMultiBirthGeneration(member) {
  const raw =
    member?.generation ??
    member?.generation_level ??
    member?.generationLevel ??
    member?.generation_number ??
    member?.gen;

  if (raw === undefined || raw === null || raw === "") return null;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? null : parsed;
}

/** Birth year, from an explicit field or from the date of birth. */
export function getMultiBirthYear(member) {
  const raw = member?.year ?? member?.birth_year ?? member?.dob_year;
  if (raw !== undefined && raw !== null && raw !== "") {
    const parsed = parseInt(raw, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }

  const dobMs = getMemberDobTimestamp(member);
  if (dobMs === -1) return null;

  return new Date(dobMs).getUTCFullYear();
}

/**
 * The set a member belongs to, as a stable key, so the siblings of one birth
 * stay together however the list is sorted. Built from the links both ways —
 * the anchor member lists the others, the others list the anchor — so every
 * member of a set produces the same key.
 */
export function getMultiBirthSetKey(member, index = 0) {
  const details = Array.isArray(member?.multiple_birth_details)
    ? member.multiple_birth_details
    : Array.isArray(member?.multiple_births_details)
      ? member.multiple_births_details
      : [];

  const ids = details
    .map((entry) => entry?.family_member_id ?? entry?.famiy_member_id ?? entry?.member_id)
    .filter((id) => id !== undefined && id !== null && id !== "")
    .map(String)
    .sort();

  return ids.length > 0 ? ids.join("|") : `own-${memberKey(member, index)}`;
}

/** Order within the set, e.g. first-born twin before second. */
export function getMultiBirthOrderNumber(member) {
  const raw =
    member?.multiple_birth_order_number ?? member?.multipleBirthOrderNumber;
  if (raw === undefined || raw === null || raw === "") return Number.MAX_SAFE_INTEGER;
  const parsed = parseInt(raw, 10);
  return Number.isNaN(parsed) ? Number.MAX_SAFE_INTEGER : parsed;
}

/**
 * id → generation level, from /family-tree-user-ids.
 *
 * The statistics payload names no generation on its member rows, and this
 * report's first column is exactly that, so the generation is looked up from the
 * one endpoint that publishes it. The map is a fallback only: a statistics row
 * that ever starts carrying its own generation keeps it.
 *
 * @param {Array<object>} familyMembers response.data.familyMembers
 */
export function buildGenerationLevelMap(familyMembers) {
  const map = new Map();

  (Array.isArray(familyMembers) ? familyMembers : []).forEach((member) => {
    const generation = getMultiBirthGeneration(member);
    if (generation === null) return;

    [member?.id, member?.family_member_id, member?.member_id]
      .filter((id) => id !== undefined && id !== null && id !== "")
      .forEach((id) => map.set(String(id), generation));
  });

  return map;
}

/** Member payloads code gender as 1 = male, 2 = female. */
const genderLetterFromCode = (raw) => {
  if (raw === undefined || raw === null) return null;
  const value = String(raw).trim().toLowerCase();
  if (value === "1" || value === "male" || value === "m") return "M";
  if (value === "2" || value === "female" || value === "f") return "F";
  return null;
};

const memberIds = (member) =>
  [member?.id, member?.family_member_id, member?.member_id]
    .filter((id) => id !== undefined && id !== null && id !== "")
    .map(String);

/** "M", "F", or null where the row itself names no gender. */
export function getMultiBirthGender(member) {
  return genderLetterFromCode(
    member?.gender ??
      member?.Gender ??
      member?.member_gender ??
      member?.memberGender
  );
}

/**
 * id → "M" / "F", for rows that ship without a `gender` of their own.
 *
 * The statistics payload already sorts everyone into
 * `total_male_female.males` / `.females`, which is the surest source; the
 * /family-tree-user-ids rows fill in anyone that section leaves out.
 *
 * @param {object} data the `data` block of /dashboard-statistics-with-details
 * @param {Array<object>} familyMembers response.data.familyMembers
 */
export function buildGenderMap(data, familyMembers) {
  const map = new Map();

  (Array.isArray(familyMembers) ? familyMembers : []).forEach((member) => {
    const gender = getMultiBirthGender(member);
    if (gender) memberIds(member).forEach((id) => map.set(id, gender));
  });

  const sections = data?.total_male_female || {};
  [
    [sections.males, "M"],
    [sections.females, "F"],
  ].forEach(([list, gender]) => {
    (Array.isArray(list) ? list : []).forEach((member) =>
      memberIds(member).forEach((id) => map.set(id, gender))
    );
  });

  return map;
}
